/**
 * Comprime un'immagine lato client tramite Canvas.
 * - Se il file NON è un'immagine (es. PDF) o è già piccolo, ritorna così com'è.
 * - Per JPEG/PNG/WEBP: ridimensiona se più largo di `maxDimension` e
 *   ricomprime in JPEG con `quality`.
 *
 * @param {File} file - Il file selezionato dall'utente
 * @param {object} opts
 * @param {number} opts.maxDimension - lato massimo in px (default 2400)
 * @param {number} opts.quality      - 0..1, JPEG quality (default 0.82)
 * @param {number} opts.thresholdMB  - sotto questa soglia non comprime (default 3)
 * @returns {Promise<File>}
 */
export async function compressImageIfNeeded(
    file,
    { maxDimension = 2400, quality = 0.82, thresholdMB = 3 } = {}
) {
    if (!file) return file;
    const isImage = file.type.startsWith("image/");
    if (!isImage) return file;
    if (file.size <= thresholdMB * 1024 * 1024) return file;

    return new Promise((resolve) => {
        const img = new Image();
        img.onerror = () => resolve(file); // fallback: ritorna originale
        img.onload = () => {
            try {
                let { width, height } = img;
                const ratio = Math.min(
                    maxDimension / width,
                    maxDimension / height,
                    1
                );
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob(
                    (blob) => {
                        if (!blob) return resolve(file);
                        const baseName = file.name.replace(/\.[^.]+$/, "");
                        const compressed = new File(
                            [blob],
                            `${baseName}.jpg`,
                            {
                                type: "image/jpeg",
                                lastModified: Date.now(),
                            }
                        );
                        // Se compressione peggiora, conserva originale
                        resolve(compressed.size < file.size ? compressed : file);
                    },
                    "image/jpeg",
                    quality
                );
            } catch {
                resolve(file);
            }
        };
        img.src = URL.createObjectURL(file);
    });
}
